import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { legacyOffersAPI, customersAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, FileText } from "lucide-react";

export default function LegacyOfferCreatePage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    document_type: "angebot",
    offer_reference: "",
    project_number: "",
    document_date: new Date().toISOString().split("T")[0],
    net_amount: 0,
    vat_rate: 19,
    vat_amount: 0,
    gross_amount: 0,
    discount_type: "keine",
    discount_value: 0,
    free_text_header: "",
    free_text_body: "",
    free_text_footer: "",
    notes_internal: ""
  });

  useEffect(() => {
    loadCustomer();
  }, [customerId]);

  const loadCustomer = async () => {
    try {
      const response = await customersAPI.get(customerId);
      setCustomer(response.data);
    } catch (error) {
      console.error("Failed to load customer:", error);
      toast.error("Kunde konnte nicht geladen werden");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate VAT and gross when net changes
      if (field === "net_amount" || field === "vat_rate") {
        const net = field === "net_amount" ? value : prev.net_amount;
        const rate = field === "vat_rate" ? value : prev.vat_rate;
        updated.vat_amount = Math.round(net * (rate / 100) * 100) / 100;
        updated.gross_amount = Math.round((net + updated.vat_amount) * 100) / 100;
      }
      
      return updated;
    });
  };

  const handleSave = async () => {
    if (!formData.title) {
      toast.error("Bitte geben Sie einen Titel ein");
      return;
    }
    
    try {
      setSaving(true);
      
      const response = await legacyOffersAPI.create({
        customer_id: customerId,
        ...formData
      });
      
      toast.success("Legacy-Angebot erstellt");
      navigate(`/legacy-angebote/${response.data.id}`);
      
    } catch (error) {
      console.error("Failed to create:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Neues Freitextangebot</h1>
            <p className="text-muted-foreground">
              Für {customer?.first_name} {customer?.last_name}
            </p>
          </div>
        </div>
        
        <Button onClick={handleSave} disabled={saving} size="lg" data-testid="btn-save-create">
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Erstellen
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stammdaten</CardTitle>
          <CardDescription>Grundlegende Informationen zum Angebot</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder="z.B. PV-Anlage Einfamilienhaus"
                data-testid="input-create-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Dokumenttyp</Label>
              <Select
                value={formData.document_type}
                onValueChange={(v) => handleInputChange("document_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="angebot">Angebot</SelectItem>
                  <SelectItem value="auftragsbestätigung">Auftragsbestätigung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Dokumentdatum</Label>
              <Input
                type="date"
                value={formData.document_date}
                onChange={(e) => handleInputChange("document_date", e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Angebotsnummer / Referenz</Label>
              <Input
                value={formData.offer_reference}
                onChange={(e) => handleInputChange("offer_reference", e.target.value)}
                placeholder="z.B. ANG-2024-001"
              />
            </div>
            
            <div className="space-y-2 col-span-2">
              <Label>Projektnummer (extern)</Label>
              <Input
                value={formData.project_number}
                onChange={(e) => handleInputChange("project_number", e.target.value)}
                placeholder="z.B. P-2024-001"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Beträge</CardTitle>
          <CardDescription>
            MwSt. und Brutto werden automatisch berechnet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nettobetrag (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.net_amount}
                onChange={(e) => handleInputChange("net_amount", parseFloat(e.target.value) || 0)}
                data-testid="input-create-net"
              />
            </div>
            
            <div className="space-y-2">
              <Label>MwSt.-Satz (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.vat_rate}
                onChange={(e) => handleInputChange("vat_rate", parseFloat(e.target.value) || 19)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>MwSt.-Betrag (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.vat_amount}
                readOnly
                className="bg-muted"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="font-semibold">Bruttobetrag (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.gross_amount}
                onChange={(e) => handleInputChange("gross_amount", parseFloat(e.target.value) || 0)}
                className="font-bold text-lg"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Leistungsbeschreibung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Kopftext</Label>
            <Textarea
              value={formData.free_text_header}
              onChange={(e) => handleInputChange("free_text_header", e.target.value)}
              rows={2}
              placeholder="Einleitungstext..."
            />
          </div>
          
          <div className="space-y-2">
            <Label>Haupttext / Positionen</Label>
            <Textarea
              value={formData.free_text_body}
              onChange={(e) => handleInputChange("free_text_body", e.target.value)}
              rows={8}
              placeholder="Leistungsbeschreibung, Positionen..."
              data-testid="textarea-create-body"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Fußtext</Label>
            <Textarea
              value={formData.free_text_footer}
              onChange={(e) => handleInputChange("free_text_footer", e.target.value)}
              rows={2}
              placeholder="Zahlungsbedingungen, Abschlusstext..."
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Interne Notizen</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.notes_internal}
            onChange={(e) => handleInputChange("notes_internal", e.target.value)}
            rows={3}
            placeholder="Interne Notizen (nicht auf Dokumenten sichtbar)..."
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pb-6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          Abbrechen
        </Button>
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileText className="h-4 w-4 mr-2" />
          )}
          Freitextangebot erstellen
        </Button>
      </div>
    </div>
  );
}
